import React from 'react';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';
import { Form, Formik, FormikProps } from 'formik';

import i18n from '../../utilities/i18n';
import log from '../../utilities/log';
import mediator from '../../utilities/mediator';
import { AuthenticateResponse } from '../../models';
import {
  Button,
  Card,
  LinkText,
  Text,
  VStack,
} from '@adc/parallax-component-library';
import TextInputWithError from '../ui/forms/TextInputWithError';
import PasswordInputWithError from '../ui/forms/PasswordInputWithError';
import MessageBanner from '../messageBanner/MessageBanner';
import PreventSubmitWrapper from '../ui/forms/PreventSubmitWrapper';
import showUnderDevelopmentWarning from '../../utilities/underDevelopmentWarning';

export default function LoginForm() {
  const navigate = useNavigate();

  const initialValues = {
    email: '',
    password: '',
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string().required(i18n.t('Auth_LoginForm.emailRequired')),
    password: Yup.string().required(i18n.t('Auth_LoginForm.passwordRequired')),
  });

  const onSubmit = (values, { setStatus }) => {
    return new Promise<void>((resolve): void => {
      mediator
        .request<AuthenticateResponse>('auth:authenticate', values)
        .then((result) => {
          if (!result.loggedIn) {
            setStatus(i18n.t('Auth_LoginForm.notLoggedIn'));
          } else {
            setStatus('');
            navigate('/');
          }

          resolve();
        })
        .catch((err) => {
          log.error(err);
          setStatus(i18n.t('Auth_LoginForm.unexpectedIssue'));
          resolve();
        });
    });
  };

  const navigateToForgotPassword = () => {
    // TODO: Remove flag after CSP-1676 is complete
    if (window.enableIncompleteFeatures) {
      navigate('/auth/forgot-password');
    } else {
      showUnderDevelopmentWarning();
    }
  };

  return (
    <VStack alignSelf="center" margin="auto">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {(formikProps: FormikProps<any>) => (
          <Form id="login-form" noValidate>
            <Card>
              <VStack margin="$4" gap="$8">
                <VStack alignItems="center">
                  <Text type="$h2.strong">
                    {i18n.t('Auth_LoginForm.title')}
                  </Text>
                </VStack>
                {formikProps.status ? (
                  <VStack marginBottom={'$4'}>
                    <MessageBanner
                      type="danger"
                      iconSize="M"
                      bodyText={i18n.t(formikProps.status)}
                      testID="status-message"
                    />
                  </VStack>
                ) : null}
                <PreventSubmitWrapper>
                  <TextInputWithError
                    name="email"
                    label={i18n.t('Auth_LoginForm.email')}
                    testID="email-input"
                  />

                  <PasswordInputWithError
                    label={i18n.t('Auth_LoginForm.password')}
                    name="password"
                    autocomplete="current-password"
                    testID="password-input"
                  />
                </PreventSubmitWrapper>

                <VStack alignItems="center" gap="$2">
                  <Button
                    disabled={formikProps.isSubmitting || !formikProps.isValid}
                    label={i18n.t('Auth_LoginForm.continue')}
                    onPress={formikProps.submitForm}
                    variant="primary"
                    testID="continue-button"
                  />
                  <LinkText
                    onPress={navigateToForgotPassword}
                    type="$bodySmall.default"
                    testID="forgot-password-link"
                  >
                    {i18n.t('Auth_LoginForm.forgotPassword')}
                  </LinkText>
                </VStack>
              </VStack>
            </Card>
          </Form>
        )}
      </Formik>
    </VStack>
  );
}
